/**
 * Dialog zum Erstellen/Bearbeiten einer Anlage
 * Lädt Hersteller/Typ-Optionen beim Öffnen
 */
import React, { useState, useCallback, useRef, useEffect } from "react";
import { Grid } from "@mui/material";
import DialogTemplate from "../../../layout/DialogTemplate";
import Button from "../../../common/inputs/Button";
import Dropdown from "../../../common/inputs/Dropdown";
import InputField from "../../../common/inputs/InputField";
import InputFieldNumber from "../../../common/inputs/InputFieldNumber";
import {
  generationUnitsApi,
  HerstellerTypOptions,
} from "../../../../services/api/generationUnits";
import { GenerationUnit, GenerationUnitCreate } from "../../../../types";
import {
  anlagentypenApi,
  Anlagentyp,
} from "../../../../services/api/anlagentypen";
import AusstattungFelder from "../components/AusstattungFelder";
import WasserUndKlimaschutzFelder from "../components/WasserUndKlimaschutzFelder";
import StoffListeSection from "../components/StoffListeSection";
import BetriebsmodusListeSection from "../components/BetriebsmodusListeSection";
import { useWertelisten } from "../hooks/useWertelisten";
import { useAnlageListen } from "../hooks/useAnlageListen";
import SubSection from "../../../layout/SubSection";

/** Betriebsinterne Bezeichnung im Genehmigungsantrag, gebildet aus der Anlagen-Nr. */
export const bezeichnungAusAnlagenNr = (anlagenNr: string): string =>
  `WEA ${anlagenNr}`;

const INITIAL_FORM: GenerationUnitCreate & { anzahl?: number } = {
  anlagen_nr: "",
  bezeichnung: "",
  hersteller: "",
  typ: "",
  rotorradius: undefined,
  sicherungsradius: undefined,
  nennleistung: undefined,
  referenzertrag: undefined,
  nabenhöhe: undefined,
  anzahl: 1,
};

interface GenerationUnitDialogProps {
  open: boolean;
  projectId: string;
  mode: "create" | "edit";
  editingUnit: GenerationUnit | null;
  /** Vorschlag für eine neue Anlage – die nächste freie Nummer des Projekts */
  naechsteAnlagenNr: string;
  onClose: () => void;
  onSubmit: (formData: GenerationUnitCreate, anzahl?: number) => Promise<void>;
}

const GenerationUnitDialog: React.FC<GenerationUnitDialogProps> = ({
  open,
  projectId,
  mode,
  editingUnit,
  naechsteAnlagenNr,
  onClose,
  onSubmit,
}) => {
  const [form, setForm] = useState<GenerationUnitCreate & { anzahl?: number }>(
    INITIAL_FORM,
  );
  const [saving, setSaving] = useState(false);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [herstellerTypOptions, setHerstellerTypOptions] =
    useState<HerstellerTypOptions>({});
  // Die Optionen-Abfrage liefert nur die fuenf technischen Daten. Fuer die Vorbelegung der
  // kopierten Abschnitte braucht der Dialog den vollstaendigen Anlagentyp.
  const [anlagentypen, setAnlagentypen] = useState<Anlagentyp[]>([]);
  const { wertelisten } = useWertelisten();
  const formRef = useRef(form);
  useEffect(() => {
    formRef.current = form;
  }, [form]);

  // Optionen erst beim Öffnen des Dialogs laden
  useEffect(() => {
    if (open) {
      generationUnitsApi.getGenerationUnitOptions().then((data) => {
        setHerstellerTypOptions(data);
      });
      anlagentypenApi.getAnlagentypen().then(setAnlagentypen);
    }
  }, [open]);

  // Form zurücksetzen/initialisieren bei Öffnen
  useEffect(() => {
    if (open) {
      if (mode === "edit" && editingUnit) {
        // Anlagen aus der Zeit vor der Nummerierung tragen noch keine – sie bekommen hier
        // denselben Vorschlag wie eine neue, sonst wäre das Pflichtfeld leer und die Anlage
        // ohne Zutun nicht mehr speicherbar.
        const nummer = editingUnit.anlagen_nr || naechsteAnlagenNr;
        setForm({
          anlagen_nr: nummer,
          bezeichnung:
            editingUnit.bezeichnung || bezeichnungAusAnlagenNr(nummer),
          hersteller: editingUnit.hersteller ?? "",
          typ: editingUnit.typ ?? "",
          rotorradius: editingUnit.rotorradius,
          sicherungsradius: editingUnit.sicherungsradius,
          nennleistung: editingUnit.nennleistung,
          referenzertrag: editingUnit.referenzertrag,
          nabenhöhe: editingUnit.nabenhöhe,
        });
      } else {
        setForm({
          ...INITIAL_FORM,
          anlagen_nr: naechsteAnlagenNr,
          bezeichnung: bezeichnungAusAnlagenNr(naechsteAnlagenNr),
          anzahl: 1,
        });
      }
      setFormErrors({});
    }
  }, [open, mode, editingUnit, naechsteAnlagenNr]);

  // Beim Anlegen mehrerer Anlagen auf einmal wären Nummer und Bezeichnung für alle dieselben.
  // Das Backend vergibt sie dann fortlaufend; die Felder bleiben leer und gesperrt. Die
  // Seriennummer gehört in dieselbe Gruppe: Sie ist die Nummer der einzelnen Turbine.
  const mehrfachAnlage = mode === "create" && (form.anzahl ?? 1) > 1;

  // Vorlage hinter dem Textpaar Hersteller/Typ – dieselbe Auflösung wie im Backend.
  const gewaehlterAnlagentyp =
    anlagentypen.find(
      (a) => a.hersteller === form.hersteller && a.typ === form.typ,
    ) ?? null;

  const listen = useAnlageListen({
    projectId,
    unitId: mode === "edit" ? (editingUnit?.id ?? null) : null,
    anlagentypId: mode === "create" ? (gewaehlterAnlagentyp?.id ?? null) : null,
  });

  /**
   * Schreibt ein Feld der Abschnitte in das Formular. Die Zahlenfelder liefern Strings; leer
   * heißt „nicht gepflegt" und wird zu null, nicht zu 0 – die Unterscheidung braucht der Antrag.
   */
  const setzeAbschnittsfeld = useCallback(
    (feld: string, wert: string | number | boolean | null) => {
      const zahl =
        typeof wert === "string" && wert !== "" && !isNaN(Number(wert))
          ? Number(wert)
          : wert === ""
            ? null
            : wert;
      setForm((f) => ({ ...f, [feld]: zahl }));
    },
    [],
  );

  const handleAnzahlChange = useCallback(
    (val: string) => {
      const eingabe = parseInt(val, 10);
      const anzahl = isNaN(eingabe) || eingabe < 1 ? 1 : Math.min(99, eingabe);
      setForm((f) => ({
        ...f,
        anzahl,
        anlagen_nr: anzahl > 1 ? "" : naechsteAnlagenNr,
        bezeichnung:
          anzahl > 1 ? "" : bezeichnungAusAnlagenNr(naechsteAnlagenNr),
      }));
    },
    [naechsteAnlagenNr],
  );

  // Die Bezeichnung folgt der Nummer, solange sie nicht von Hand geändert wurde – sonst
  // stünde nach dem Ändern der Nummer weiter die alte Bezeichnung da.
  const handleAnlagenNrChange = useCallback((val: string) => {
    setForm((f) => ({
      ...f,
      anlagen_nr: val,
      bezeichnung:
        f.bezeichnung === bezeichnungAusAnlagenNr(f.anlagen_nr ?? "")
          ? bezeichnungAusAnlagenNr(val)
          : f.bezeichnung,
    }));
  }, []);

  const validateForm = useCallback((): boolean => {
    const errors: Record<string, string> = {};
    if (!form.hersteller?.trim())
      errors.hersteller = "Hersteller ist erforderlich";
    if (!form.typ?.trim()) errors.typ = "Typ ist erforderlich";
    if (!mehrfachAnlage) {
      if (!form.anlagen_nr?.trim())
        errors.anlagen_nr = "Anlagen-Nr. ist erforderlich";
      if (!form.bezeichnung?.trim())
        errors.bezeichnung = "Bezeichnung ist erforderlich";
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  }, [form, mehrfachAnlage]);

  const handleSubmit = useCallback(async () => {
    if (!validateForm()) return;

    (document.activeElement as HTMLElement)?.blur();

    setSaving(true);
    try {
      await new Promise((r) => setTimeout(r, 0));
      const { anzahl: _anzahl, ...felder } = formRef.current;
      const item: GenerationUnitCreate = {
        ...felder,
        // Leer gelassen vergibt das Backend Nummer und Bezeichnung fortlaufend.
        anlagen_nr: felder.anlagen_nr?.trim() || undefined,
        bezeichnung: felder.bezeichnung?.trim() || undefined,
        hersteller: felder.hersteller?.trim() || undefined,
        typ: felder.typ?.trim() || undefined,
      };
      if (mode === "create") {
        // Die Listen gehen nur beim Anlegen mit: Danach werden sie ueber ihre eigenen
        // Endpunkte gepflegt, jede Zeile einzeln und sofort.
        item.stoffe = listen.stoffe.map(
          ({ id: _id, generation_unit_id: _gid, ...rest }) => rest,
        );
        item.betriebsmodi = listen.betriebsmodi.map(
          ({ id: _id, generation_unit_id: _gid, ...rest }) => rest,
        );
      }
      if (mode === "create") {
        const anzahl = Math.max(
          1,
          Math.min(99, Math.floor(formRef.current.anzahl ?? 1)),
        );
        await onSubmit(item, anzahl);
      } else {
        await onSubmit(item);
      }
      onClose();
    } finally {
      setSaving(false);
    }
  }, [
    mode,
    validateForm,
    onSubmit,
    onClose,
    listen.stoffe,
    listen.betriebsmodi,
  ]);

  return (
    <DialogTemplate
      open={open}
      onClose={onClose}
      title={mode === "create" ? "Neue Anlage" : "Anlage bearbeiten"}
      maxWidth="md"
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Abbrechen
          </Button>
          <Button variant="primary" onClick={handleSubmit} disabled={saving}>
            {saving ? "Speichern..." : "Speichern"}
          </Button>
        </>
      }
    >
      {/* Einziger Grid-Container – spacing liefert row- und column-gap automatisch */}
      {/*
        Hersteller und Typ stehen über den Abschnitten: Sie entscheiden, welche Vorlage die
        Anlage bekommt, und wirken damit auf alles darunter. Der untere Abstand ist derselbe,
        den die Unter-Abschnitte untereinander halten (SubSection setzt mb: 5).
      */}
      <Grid container spacing={3} sx={{ mt: 0.5, mb: 5 }}>
        {/* Hersteller + Typ */}
        <Grid size={{ xs: 12, sm: 6 }}>
          <Dropdown
            label="Hersteller"
            value={form.hersteller ?? ""}
            options={Object.keys(herstellerTypOptions).sort()}
            onChange={(val) =>
              setForm((f) => ({
                ...f,
                hersteller: val,
                typ: "",
              }))
            }
            allowCustomInput={true}
            customInputLabel="Anderen Hersteller eingeben..."
            customInputType="text"
            customInputPlaceholder="Hersteller eingeben..."
            error={!!formErrors.hersteller}
            helperText={formErrors.hersteller}
            variant="floating"
            size="small"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Dropdown
            label="Typ"
            value={form.typ ?? ""}
            options={
              form.hersteller
                ? (herstellerTypOptions[form.hersteller] ?? []).map(
                    (t) => t.typ,
                  )
                : []
            }
            onChange={(val) => {
              setForm((f) => {
                const typOptions = f.hersteller
                  ? (herstellerTypOptions[f.hersteller] ?? [])
                  : [];
                const matched = typOptions.find((t) => t.typ === val);
                return matched
                  ? {
                      ...f,
                      typ: val,
                      rotorradius: matched.rotorradius,
                      sicherungsradius: matched.sicherungsradius,
                      nennleistung: matched.nennleistung,
                      referenzertrag: matched.referenzertrag,
                      nabenhöhe: matched.nabenhöhe,
                    }
                  : { ...f, typ: val };
              });
            }}
            allowCustomInput={true}
            customInputLabel="Anderen Typ eingeben..."
            customInputType="text"
            customInputPlaceholder="Typ eingeben..."
            error={!!formErrors.typ}
            helperText={formErrors.typ}
            variant="floating"
            size="small"
          />
        </Grid>

        {/* Anzahl (nur im Create-Modus) */}
        {mode === "create" && (
          <Grid size={{ xs: 12, sm: 6 }}>
            <InputFieldNumber
              label="Anzahl"
              value={String(form.anzahl ?? 1)}
              onChange={handleAnzahlChange}
              variant="floating"
              allowDecimals={false}
            />
          </Grid>
        )}
      </Grid>

      {/*
        Die Angaben zur einzelnen Anlage. Bewusst nicht „Stammdaten" genannt: Das ist in
        diesem Projekt der Anlagentyp, die mandantenübergreifende Vorlage – dieselbe Vokabel
        hier würde genau die Grenze verwischen, um die es geht.
      */}
      <SubSection title="Technische Daten" defaultExpanded>
        <Grid container spacing={3}>
          {/*
            Bezeichnung zuerst: Sie ist der sprechende Name, unter dem die Anlage in jedem
            Fachformular auftaucht. Die Anlagen-Nr. daneben ist der Schlüssel im
            ELiA-Anlagenbaum und im Format an das Bundesland gebunden.
          */}
          <Grid size={{ xs: 12, sm: 6 }}>
            <InputField
              label="Bezeichnung"
              value={form.bezeichnung ?? ""}
              onChange={(val) => setForm((f) => ({ ...f, bezeichnung: val }))}
              placeholder={
                mehrfachAnlage
                  ? "wird aus der Nummer gebildet"
                  : "z. B. WEA A001"
              }
              readOnly={mehrfachAnlage}
              required={!mehrfachAnlage}
              error={formErrors.bezeichnung}
              size="small"
              tooltip="Betriebsinterne Bezeichnung der Anlage im Genehmigungsantrag. Folgt der Anlagen-Nr., solange sie nicht von Hand geändert wird."
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <InputField
              label="Anlagen-Nr. (ELiA)"
              value={form.anlagen_nr ?? ""}
              onChange={handleAnlagenNrChange}
              placeholder={
                mehrfachAnlage ? "wird fortlaufend vergeben" : "z. B. A001"
              }
              readOnly={mehrfachAnlage}
              required={!mehrfachAnlage}
              error={formErrors.anlagen_nr}
              size="small"
              tooltip="Nummer des Anlagenteils in ELiA. Anders als die Bezeichnung ist sie im Format gebunden: im Regelfall drei Ziffern, in Nordrhein-Westfalen vier, in Bayern bis zu zwölf Zeichen. Sie wird mit der Behörde abgestimmt und bleibt bei Folgeanträgen gleich."
            />
          </Grid>

          {/* Seriennummer – gehört zur einzelnen Turbine, deshalb nicht beim Mehrfach-Anlegen */}
          {!mehrfachAnlage && (
            <Grid size={{ xs: 12, sm: 6 }}>
              <InputField
                label="Seriennummer"
                value={form.seriennummer ?? ""}
                onChange={(val) =>
                  setForm((f) => ({ ...f, seriennummer: val }))
                }
                size="small"
                tooltip="Seriennummer der einzelnen Anlage (ELiA 16.1.9). Steht erst nach der Bestellung fest und ist im Antrag regelmäßig leer."
              />
            </Grid>
          )}

          {/* Nennleistung + Referenzertrag */}
          <Grid size={{ xs: 12, sm: 6 }}>
            <InputFieldNumber
              label="Nennleistung"
              value={String(form.nennleistung ?? "")}
              onChange={(val) =>
                setForm((f) => ({
                  ...f,
                  nennleistung: val ? parseFloat(val) : undefined,
                }))
              }
              suffix="MW"
              variant="floating"
              allowDecimals={true}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <InputFieldNumber
              label="Referenzertrag"
              value={String(form.referenzertrag ?? "")}
              onChange={(val) =>
                setForm((f) => ({
                  ...f,
                  referenzertrag: val ? parseFloat(val) : undefined,
                }))
              }
              suffix="MWh"
              variant="floating"
              allowDecimals={true}
            />
          </Grid>

          {/* Nabenhöhe + Rotorradius */}
          <Grid size={{ xs: 12, sm: 6 }}>
            <InputFieldNumber
              label="Nabenhöhe"
              value={String(form.nabenhöhe ?? "")}
              onChange={(val) =>
                setForm((f) => ({
                  ...f,
                  nabenhöhe: val ? parseFloat(val) : undefined,
                }))
              }
              suffix="m"
              variant="floating"
              allowDecimals={true}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <InputFieldNumber
              label="Rotorradius"
              value={String(form.rotorradius ?? "")}
              onChange={(val) =>
                setForm((f) => ({
                  ...f,
                  rotorradius: val ? parseFloat(val) : undefined,
                }))
              }
              suffix="m"
              variant="floating"
              allowDecimals={true}
            />
          </Grid>

          {/* Sicherungsradius */}
          <Grid size={{ xs: 12, sm: 6 }}>
            <InputFieldNumber
              label="Sicherungsradius"
              value={String(form.sicherungsradius ?? "")}
              onChange={(val) =>
                setForm((f) => ({
                  ...f,
                  sicherungsradius: val ? parseFloat(val) : undefined,
                }))
              }
              suffix="m"
              variant="floating"
              allowDecimals={true}
            />
          </Grid>
        </Grid>
      </SubSection>

      {/*
        Die vereinbarten Abschnitte, eingeklappt. Vier davon sind dieselben Komponenten, die auch
        die Anlagentyp-Verwaltung benutzt – die Gliederung existiert damit genau einmal.
      */}
      <StoffListeSection
        stoffe={listen.stoffe}
        wertelisten={wertelisten}
        loading={listen.loading}
        onCreate={listen.createStoff}
        onUpdate={listen.updateStoff}
        onDelete={listen.deleteStoff}
      />

      {/*
        Die Pegel sind hier bearbeitbar: Die Zeile ist eine Kopie, keine Sicht auf die Vorlage.
        Anlegen ist moeglich, weil die Genehmigung einen Modus vorschreiben kann, den der
        Anlagentyp noch nicht kennt – etwa einen schallreduzierten Nachtbetrieb.
      */}
      <BetriebsmodusListeSection
        modi={listen.betriebsmodi}
        wertelisten={wertelisten}
        loading={listen.loading}
        mitBetriebsangaben
        onCreate={listen.createBetriebsmodus}
        onUpdate={listen.updateBetriebsmodus}
        onDelete={listen.deleteBetriebsmodus}
      />

      <WasserUndKlimaschutzFelder
        werte={form}
        onChange={setzeAbschnittsfeld}
        wertelisten={wertelisten}
      />

      <AusstattungFelder
        werte={form}
        onChange={setzeAbschnittsfeld}
        mitRichtfunk
      />

      <SubSection title="Flächenbilanz des Betriebsgrundstücks" columns={2}>
        <InputFieldNumber
          label="Überbaut, Bestand"
          value={String(form.flaeche_ueberbaut_vorhanden_m2 ?? "")}
          onChange={(val) =>
            setzeAbschnittsfeld("flaeche_ueberbaut_vorhanden_m2", val)
          }
          suffix="m²"
          variant="floating"
          tooltip="Fundament und Kranstellfläche (ELiA 13.1.1). Bei einer Neuanlage auf Acker null, bei einem Repowering nicht."
        />
        <InputFieldNumber
          label="Überbaut, künftig"
          value={String(form.flaeche_ueberbaut_zukuenftig_m2 ?? "")}
          onChange={(val) =>
            setzeAbschnittsfeld("flaeche_ueberbaut_zukuenftig_m2", val)
          }
          suffix="m²"
          variant="floating"
        />
        <InputFieldNumber
          label="Zuwegung, Bestand"
          value={String(form.flaeche_zuwegung_vorhanden_m2 ?? "")}
          onChange={(val) =>
            setzeAbschnittsfeld("flaeche_zuwegung_vorhanden_m2", val)
          }
          suffix="m²"
          variant="floating"
          tooltip="Zuwegung und Wendeflächen."
        />
        <InputFieldNumber
          label="Zuwegung, künftig"
          value={String(form.flaeche_zuwegung_zukuenftig_m2 ?? "")}
          onChange={(val) =>
            setzeAbschnittsfeld("flaeche_zuwegung_zukuenftig_m2", val)
          }
          suffix="m²"
          variant="floating"
        />
      </SubSection>
    </DialogTemplate>
  );
};

export default GenerationUnitDialog;
